// Tier 5 (QUANTITATIVE TRADING — Python · Statistics · ML · Live Deployment) — UI components.
// Purple themed (distinct from Tier 1+2 gold, Tier 3 emerald, Tier 4 blue).
// Gates behind Tier 4 "ALGO TRADER" graduation.
// Milestone gate = full 30 lessons passed (≥75%) + 5 self-reported "deployed quant system" items.
// Lesson 5.6.5 is the GRAND FINALE — full curriculum graduation.

import React, { useState } from "react";
import { THEME, flexxState } from "./state";
import Portal from "./Portal";
import { FlexxCertificate, openCertificatePrintView } from "./FlexxCertificate";

export const TIER5_PASS_THRESHOLD = 0.75;
export const TIER5_LESSON_XP = 75;        // first-pass XP per Tier 5 lesson
export const TIER5_QUIZ_PASS_BONUS = 12;  // +12 XP per quiz pass
export const TIER5_MODULE_BONUS = 50;     // +50 XP per module complete
export const TIER5_TIER_BONUS = 400;      // Tier 5 completion bonus (graduation = full curriculum)
export const TIER5_PHASES = ["5.1", "5.2", "5.3", "5.4", "5.5", "5.6"];

export const TIER5_PURPLE = "#a855f7";
export const TIER5_PURPLE_GLOW = "rgba(168,85,247,0.38)";
export const TIER5_PURPLE_DARK = "#7c3aed";

export const TIER5_PHASE_LABELS = {
  "5.1": "Quant Foundations & Python Setup",
  "5.2": "Probability, Statistics & Market Math",
  "5.3": "Statistical Edge & Backtesting",
  "5.4": "Python for Trading: Applied Quant",
  "5.5": "Machine Learning in Trading",
  "5.6": "Quant Systems & Live Deployment",
};

export function Tier5Header({ passed, total, unlocked, certified }) {
  return (
    <div data-testid="tier5-header" style={{
      display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap",
      marginTop: 36, marginBottom: 14, paddingTop: 18,
      borderTop: `1px solid ${THEME.border}`,
    }}>
      <div style={{ fontSize: 11, letterSpacing: 2.5, color: TIER5_PURPLE, fontFamily: "monospace", textShadow: unlocked ? `0 0 12px ${TIER5_PURPLE_GLOW}` : "none" }}>
        {unlocked ? "TIER 5 · QUANTITATIVE" : "🔒 TIER 5"}
      </div>
      <div style={{ color: THEME.text, fontSize: 22, fontWeight: 800, fontFamily: "'Bebas Neue', 'Oswald', sans-serif", letterSpacing: 0.5 }}>
        QUANTITATIVE TRADING — Python &amp; Live Quant Systems
      </div>
      {unlocked && (
        <div data-testid="tier5-progress" style={{ marginLeft: "auto", color: THEME.mute, fontFamily: "monospace", fontSize: 12 }}>
          {passed} / {total} passed
        </div>
      )}
      {certified && (
        <div data-testid="tier5-certified-pill" style={{
          marginLeft: unlocked ? 0 : "auto",
          padding: "4px 10px", border: `1px solid ${TIER5_PURPLE}`, borderRadius: 999,
          color: TIER5_PURPLE, fontFamily: "monospace", fontSize: 10, letterSpacing: 1.5,
          boxShadow: `0 0 16px ${TIER5_PURPLE_GLOW}`,
        }}>
          QUANT CERTIFIED
        </div>
      )}
    </div>
  );
}

export function Tier5GatePlaceholder() {
  return (
    <div data-testid="tier5-locked-placeholder" style={{
      marginTop: 14, padding: 22, background: "rgba(168,85,247,0.04)",
      border: `1px dashed ${TIER5_PURPLE}55`, borderRadius: 10, textAlign: "center",
    }}>
      <div style={{ fontSize: 28, marginBottom: 6 }}>🔒</div>
      <div style={{ color: TIER5_PURPLE, fontFamily: "monospace", fontSize: 10, letterSpacing: 2, marginBottom: 6 }}>
        TIER 5 · QUANTITATIVE TRADING
      </div>
      <div style={{ color: THEME.text, fontSize: 14, lineHeight: 1.6, maxWidth: 520, margin: "0 auto" }}>
        Python · Statistics · Machine Learning · Live Quant Deployment
      </div>
      <div style={{ color: THEME.mute, fontSize: 11, lineHeight: 1.6, marginTop: 8, fontFamily: "monospace" }}>
        UNLOCKS WHEN YOU COMPLETE TIER 4 (ALGO TRADER)
      </div>
      <div style={{ color: THEME.mute, fontSize: 10, lineHeight: 1.6, marginTop: 4, fontFamily: "monospace", letterSpacing: 1 }}>
        30 LESSONS · 6 MODULES · "If you can't measure it, you can't trade it."
      </div>
    </div>
  );
}

// 7-item gate: 2 auto-verified + 5 self-reported "deployed live quant system".
export function Tier5MilestoneGate({ tier5Lessons, completion, onConfirm, onClose, modalBg, modalCard, primaryBtn, ghostBtn }) {
  const allLessonsDone = tier5Lessons.every((l) => completion[l.id]?.passed);
  const allQuizzesPassedAt75 = tier5Lessons.every((l) => (completion[l.id]?.score ?? 0) >= TIER5_PASS_THRESHOLD);
  const auto = [
    { id: "all-30", label: "All 30 Tier 5 lessons completed", ok: allLessonsDone },
    { id: "all-75", label: "Every Tier 5 quiz scored ≥75%", ok: allQuizzesPassedAt75 },
  ];
  const selfItems = [
    { id: "py-env", label: "I have a working Python quant environment (pandas, numpy, vectorbt or similar)" },
    { id: "backtest", label: "I have backtested at least one strategy with realistic costs and slippage" },
    { id: "walk-forward", label: "I have run walk-forward analysis or out-of-sample validation on a strategy" },
    { id: "ml-feature", label: "I built and labeled a feature set / target for at least one ML trading model" },
    { id: "live-deploy", label: "I have deployed a quant system to paper or live trading and monitored it for ≥30 days" },
  ];
  const [checked, setChecked] = useState({});
  const allChecked = selfItems.every((i) => checked[i.id]);
  const canConfirm = auto.every((a) => a.ok) && allChecked;

  return (
    <Portal>
      <div data-testid="tier5-milestone-gate-modal" style={modalBg}>
        <div style={{ ...modalCard, maxWidth: 560 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 12 }}>
            <div>
              <div style={{ fontSize: 11, letterSpacing: 2, color: TIER5_PURPLE, fontFamily: "monospace" }}>
                TIER 5 · GRADUATION GATE
              </div>
              <div style={{ color: THEME.text, fontSize: 18, fontWeight: 800, marginTop: 4 }}>
                Quant Trader Milestone
              </div>
            </div>
            <button data-testid="tier5-gate-close" onClick={onClose} style={ghostBtn}>Close</button>
          </div>
          <div style={{ color: THEME.mute, fontSize: 12, lineHeight: 1.6, marginBottom: 12 }}>
            Two checks are auto-verified from your lesson completion data. Five are on your honor —
            this is the FINAL tier. Lying to yourself here means you signed your own permission slip
            to fail in production.
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {auto.map((a) => (
              <div key={a.id} data-testid={`tier5-gate-auto-${a.id}`}
                style={{ padding: "10px 12px", background: a.ok ? "rgba(168,85,247,0.08)" : "#181818",
                  border: `1px solid ${a.ok ? TIER5_PURPLE : THEME.border}`, borderRadius: 8,
                  display: "flex", alignItems: "center", gap: 10, fontSize: 13 }}>
                <span style={{ color: a.ok ? TIER5_PURPLE : THEME.mute, fontFamily: "monospace", fontSize: 16 }}>{a.ok ? "✓" : "○"}</span>
                <span style={{ color: a.ok ? THEME.text : THEME.mute }}>{a.label}</span>
              </div>
            ))}
            {selfItems.map((it) => (
              <label key={it.id} data-testid={`tier5-gate-item-${it.id}`}
                style={{ padding: "10px 12px", background: checked[it.id] ? "rgba(168,85,247,0.08)" : "#181818",
                  border: `1px solid ${checked[it.id] ? TIER5_PURPLE : THEME.border}`, borderRadius: 8,
                  display: "flex", alignItems: "center", gap: 10, cursor: "pointer", fontSize: 13 }}>
                <input
                  type="checkbox"
                  checked={!!checked[it.id]}
                  onChange={(e) => setChecked((s) => ({ ...s, [it.id]: e.target.checked }))}
                  data-testid={`tier5-gate-check-${it.id}`}
                  style={{ accentColor: TIER5_PURPLE }}
                />
                <span style={{ color: THEME.text }}>{it.label}</span>
              </label>
            ))}
          </div>
          <button data-testid="tier5-gate-confirm-btn"
            onClick={canConfirm ? onConfirm : undefined}
            disabled={!canConfirm}
            style={{
              ...primaryBtn, width: "100%", marginTop: 16, padding: "14px 18px", fontSize: 13,
              background: canConfirm ? TIER5_PURPLE : "#222",
              borderColor: canConfirm ? TIER5_PURPLE : THEME.border,
              color: canConfirm ? "#0a0a0a" : THEME.mute,
              cursor: canConfirm ? "pointer" : "not-allowed",
              boxShadow: canConfirm ? `0 0 24px ${TIER5_PURPLE_GLOW}` : "none",
            }}>
            CONFIRM QUANT TRADER GRADUATION
          </button>
          {!canConfirm && (
            <div data-testid="tier5-gate-waiting" style={{ marginTop: 10, color: THEME.mute, fontSize: 11, fontFamily: "monospace", textAlign: "center", letterSpacing: 1 }}>
              {auto.every((a) => a.ok) ? "TICK ALL 5 SELF-REPORTED ITEMS TO CONTINUE" : "FINISH ALL 30 LESSONS AT ≥75% TO UNLOCK GATE"}
            </div>
          )}
        </div>
      </div>
    </Portal>
  );
}

// Tier 5 / Full-curriculum graduation certificate modal.
export function Tier5CertificateModal({ onClose, modalBg, modalCard, primaryBtn, ConfettiComp }) {
  const certs = flexxState.tierCerts() || {};
  const cert = certs["5"] || null;
  const profile = flexxState.profile() || {};
  const issued = flexxState.tier5Graduate ? flexxState.tier5Graduate() : null;
  const onDownload = () => {
    if (!cert) return;
    openCertificatePrintView({
      tier: "quant",
      certId: cert.cert_id, signature: cert.signature, issuedAt: cert.issued_at,
      verifyUrl: cert.verify_url || cert.verify_path, badge: cert.badge,
      userName: cert.user_name || profile.fullName || profile.email || "FLEXX Trader",
      courseName: "FLEXX Academy — Full Curriculum Complete",
      courseSubtitle: "Tiers 1–5 · Beginner → Quantitative Trading",
    });
  };

  return (
    <Portal>
      {ConfettiComp && <ConfettiComp />}
      <div data-testid="tier5-certificate-modal" style={modalBg}>
        <div style={{ ...modalCard, maxWidth: 720, padding: 24, position: "relative" }}>
          <button data-testid="tier5-cert-close" onClick={onClose}
            style={{ position: "absolute", top: 12, right: 12, background: "transparent", border: "none", color: THEME.mute, fontSize: 22, cursor: "pointer", lineHeight: 1 }}>×</button>
          <div style={{ textAlign: "center", marginBottom: 16 }}>
            <div style={{ fontSize: 11, letterSpacing: 3, color: TIER5_PURPLE, fontFamily: "monospace", textShadow: `0 0 12px ${TIER5_PURPLE_GLOW}` }}>
              🎓 FLEXX FULL CURRICULUM COMPLETE
            </div>
            <div style={{ color: THEME.text, fontSize: 22, fontWeight: 800, marginTop: 6, fontFamily: "'Bebas Neue', 'Oswald', sans-serif", letterSpacing: 1 }}>
              You finished every tier from Beginner to Quant.
            </div>
            <div style={{ color: THEME.mute, fontSize: 12, lineHeight: 1.6, marginTop: 6, maxWidth: 520, margin: "6px auto 0" }}>
              Tier 1 Foundations · Tier 2 SuPER · Tier 3 AI Trader · Tier 4 Algo Trader · Tier 5 Quant.
              You no longer need permission to call yourself a trader.
            </div>
          </div>
          <FlexxCertificate
            tier="quant"
            userName={cert?.user_name || profile.fullName || profile.email || "FLEXX Trader"}
            courseName="FLEXX Academy — Full Curriculum Complete"
            courseSubtitle="Tiers 1–5 · Beginner → Quantitative Trading"
            certId={cert?.cert_id}
            signature={cert?.signature}
            issuedAt={cert?.issued_at || (issued ? new Date(issued).toISOString() : undefined)}
            verifyUrl={cert?.verify_url || cert?.verify_path}
            badge={cert?.badge || "QUANT CERTIFIED"}
          />
          <div style={{ display: "flex", gap: 10, marginTop: 18, justifyContent: "center", flexWrap: "wrap" }}>
            <button data-testid="tier5-cert-download-btn" onClick={onDownload}
              style={{ ...primaryBtn, background: TIER5_PURPLE, borderColor: TIER5_PURPLE, color: "#0a0a0a",
                padding: "12px 20px", fontSize: 12, boxShadow: `0 0 24px ${TIER5_PURPLE_GLOW}` }}>
              DOWNLOAD GRAND DIPLOMA
            </button>
            <button data-testid="tier5-cert-share-btn"
              onClick={async () => {
                const url = cert?.verify_url || cert?.verify_path || window.location.origin;
                const text = `I just completed the full FLEXX Academy curriculum — all 5 tiers, Beginner through Quant. ${url}`;
                if (navigator.share) {
                  try { await navigator.share({ title: "FLEXX Quant Graduate", text, url }); } catch (_) {}
                } else if (navigator.clipboard) {
                  try { await navigator.clipboard.writeText(text); } catch (_) {}
                }
              }}
              style={{ padding: "12px 20px", fontSize: 12, background: "transparent",
                border: `1px solid ${TIER5_PURPLE}`, color: TIER5_PURPLE, borderRadius: 8, cursor: "pointer", fontFamily: "monospace", letterSpacing: 1 }}>
              SHARE YOUR ACHIEVEMENT
            </button>
          </div>
        </div>
      </div>
    </Portal>
  );
}
